import type { PictoChartItemData } from './pictoChart.type';
import { DEFAULT_SIZE } from './themes/PictoChartStyles.css';

const GAP_RATIO = 0.25;

function _ceil(a: number, b: number) {
  return Math.ceil(a / b) * b;
}

export function getFlowingInfo<K, D extends PictoChartItemData<K>>(
  items: D[],
  isVert: boolean,
  width: number,
  height: number,
  isFlowingWidth: boolean,
  isFlowingHeight: boolean,
  colCount?: number,
  rowCount?: number,
  colWidth?: number,
  rowHeight?: number
) {
  let numCells = 0;
  let maxColSpan = 1;
  let maxRowSpan = 1;
  let minSpan = Infinity;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const columnSpan = item.columnSpan || 1;
    const rowSpan = item.rowSpan || 1;
    if (columnSpan <= 0 || rowSpan <= 0) continue;

    // Compute the maximum columnSpan and rowSpan
    if (columnSpan > maxColSpan) maxColSpan = columnSpan;
    if (rowSpan > maxRowSpan) maxRowSpan = rowSpan;

    // Compute minimum span for gap computation
    if (columnSpan < minSpan) minSpan = columnSpan;
    if (rowSpan < minSpan) minSpan = rowSpan;

    numCells += columnSpan * rowSpan * (item.count || 1);
  }

  if (numCells === 0) return {};

  // Default colWidth and rowHeight for flowing layout
  if (isFlowingWidth || isFlowingHeight) {
    if (!colWidth) colWidth = rowHeight ? rowHeight : DEFAULT_SIZE;
    if (!rowHeight) rowHeight = colWidth;
  }

  // Default colCount and rowCount for both layouts
  // We set the default colCount (or rowCount) to be an integer multiple of the maxColSpan (or maxRowSpan) to
  // ensure that all the items can find a spot to occupy in the mixed sizes case.
  if (!colCount && !rowCount) {
    // Try to achieve square cells, (width / colCount) = (height / rowCount)
    if (!isFlowingWidth && !isFlowingHeight) {
      if (isVert) rowCount = _ceil(Math.sqrt((numCells * height) / width), maxRowSpan);
      else colCount = _ceil(Math.sqrt((numCells * width) / height), maxColSpan);
    } else if (!isFlowingWidth) colCount = Math.max(Math.floor(width / (colWidth || 1)), 1);
    else if (!isFlowingHeight) rowCount = Math.max(Math.floor(height / (rowHeight || 1)), 1);
    else {
      if (isVert) rowCount = _ceil(Math.sqrt(numCells), maxRowSpan);
      else colCount = _ceil(Math.sqrt(numCells), maxColSpan);
    }
  }

  // Now only either colCount or rowCount is undefined
  if (!colCount) colCount = _ceil(numCells / (rowCount || 1), maxColSpan);
  else if (!rowCount) rowCount = _ceil(numCells / colCount, maxRowSpan);

  const gridGap = GAP_RATIO * minSpan;
  // Default colWidth and rowHeight for fixed layout
  if (!isFlowingWidth && !isFlowingHeight) {
    if (!colWidth)
      colWidth = rowHeight ? rowHeight : Math.min(width / colCount, height / (rowCount || 1));
    if (!rowHeight) rowHeight = colWidth;
  }

  if (
    colCount <= 0 ||
    !rowCount ||
    rowCount <= 0 ||
    !colWidth ||
    colWidth <= 0 ||
    !rowHeight ||
    rowHeight <= 0
  )
    return {};

  return {
    calcColCount: colCount,
    calcRowCount: rowCount,
    calcColWidth: colWidth ? colWidth - gridGap : colWidth,
    calcRowHeight: rowHeight ? rowHeight - gridGap : rowHeight,
    gridGap: gridGap
  };
}

/**
 * Returns whether the pictoChart is layout from bottom to top.
 */
const isOriginBottom = (origin: string) => {
  return origin === 'bottomStart' || origin === 'bottomEnd';
};

/**
 * Returns whether the pictoChart is layout from right to left.
 */
const isOriginRight = (origin: string) => {
  const isEnd = origin === 'topEnd' || origin === 'bottomEnd';
  return isEnd;
};

/**
 * Returns whether the cells are available given col, row and columnSpan, rowSpan needed.
 */
const areCellsAvailable = (
  map: boolean[][],
  col: number,
  row: number,
  columnSpan: number,
  rowSpan: number
) => {
  for (let r = 0; r < rowSpan; r++) {
    for (let c = 0; c < columnSpan; c++) {
      if (map[row + r][col + c]) return false;
    }
  }
  return true;
};

/**
 * Occupies the cell in the map given col, row and columnSpan, rowSpan.
 */
const occupyCells = (
  map: boolean[][],
  col: number,
  row: number,
  columnSpan: number,
  rowSpan: number
) => {
  for (let r = 0; r < rowSpan; r++) {
    for (let c = 0; c < columnSpan; c++) {
      map[row + r][col + c] = true;
    }
  }
};

/**
 * Returns whether the cells are available given col, row and the colCount and rowCount of the pictochart.
 */
const findNextAvailableCell = (
  map: boolean[][],
  columnSpan: number,
  rowSpan: number,
  colCount: number,
  rowCount: number,
  isVert: boolean
): { row: number; col: number } | undefined => {
  if (isVert) {
    for (let c = 0; c <= colCount - columnSpan; c++) {
      for (let r = 0; r <= rowCount - rowSpan; r++) {
        if (areCellsAvailable(map, c, r, columnSpan, rowSpan)) {
          occupyCells(map, c, r, columnSpan, rowSpan);
          return { col: c, row: r };
        }
      }
    }
    return;
  }
  
  for (let r = 0; r < rowCount - rowSpan + 1; r++) {
    for (let c = 0; c < colCount - columnSpan + 1; c++) {
      if (areCellsAvailable(map, c, r, columnSpan, rowSpan)) {
        occupyCells(map, c, r, columnSpan, rowSpan);
        return { col: c, row: r };
      }
    }
  }

  return;
};

/**
 * Returns the picto items with row and col of the pictochart.
 */
export function generateElementCoords<K, D extends PictoChartItemData<K>>(
  items: D[],
  row: number,
  column: number,
  layoutOrigin: 'topStart' | 'topEnd' | 'bottomStart' | 'bottomEnd',
  isVert: boolean) {
  const rowCount = row;
  const colCount = column;
  const isLayoutBottom = isOriginBottom(layoutOrigin);
  const isLayoutRight = isOriginRight(layoutOrigin);
  const pictoItems = [];

  const map: boolean[][] = new Array(rowCount);
  for (let i = 0; i < rowCount; i++) {
    map[i] = new Array(colCount).fill(false);
  }

  const isFractionalLeft = true;
  let lastFractionalCell = {
    row: -1, 
    col: -1, 
    count: 0, 
    direction: isFractionalLeft,
    rowSpan: 1,
    columnSpan: 1
   };

  for (let i = 0; i < items.length; i++) {
    const currItem = items[i];
    const rowSpan = currItem.rowSpan ?? 1;
    const columnSpan = currItem.columnSpan ?? 1;
    let totalCount = currItem.count ?? 1;

    // fill previous fractional cell if needed
    if (lastFractionalCell.count > 0 && lastFractionalCell.count < 1
      && lastFractionalCell.rowSpan === rowSpan &&
      lastFractionalCell.columnSpan === columnSpan) {
      const amountToFill = 1 - lastFractionalCell.count;
      const fillAmount = Math.min(amountToFill, totalCount);

      const filledRow = isLayoutBottom
        ? rowCount - lastFractionalCell.row - rowSpan
        : lastFractionalCell.row;
      const filledCol = isLayoutRight
        ? colCount - lastFractionalCell.col - columnSpan
        : lastFractionalCell.col;

      pictoItems.push({
        row: filledRow,
        col: filledCol,
        countIdx: i,
        itemIdx: i,
        item: currItem,
        fractionalStart: lastFractionalCell.count,
        fractionalEnd: lastFractionalCell.count + fillAmount
      });

      lastFractionalCell.count += fillAmount;
      totalCount -= fillAmount;

      if (lastFractionalCell.count >= 1) {
        lastFractionalCell = { row: -1, col: -1, count: 0, direction: isFractionalLeft, rowSpan, columnSpan};
      } else {
        continue;
      }
    }

    // fill with full units first
    const fullUnits = Math.floor(totalCount);
    for (let j = 0; j < fullUnits; j++) {
      const cell = findNextAvailableCell(map, columnSpan, rowSpan, colCount, rowCount, isVert);
      if (cell) {
        const currRow = isLayoutBottom ? rowCount - cell.row - rowSpan : cell.row;
        const currCol = isLayoutRight ? colCount - cell.col - columnSpan : cell.col;

        pictoItems.push({
          row: currRow,
          col: currCol,
          countIdx: j,
          itemIdx: i,
          item: currItem,
          fractionalStart: 0,
          fractionalEnd: 1
        });
      }
    }

    // fill with the current item's remaining fractional cell
    const remainder = totalCount - fullUnits;
    if (remainder > 0 && remainder < 1) {
      const cell = findNextAvailableCell(map, columnSpan, rowSpan, colCount, rowCount, isVert);
      if (cell) {
        lastFractionalCell = {
          row: cell.row,
          col: cell.col,
          count: remainder,
          direction: true,
          rowSpan,
          columnSpan        
        };

        const newRow = isLayoutBottom ? rowCount - cell.row - rowSpan : cell.row;
        const newCol = isLayoutRight ? colCount - cell.col - columnSpan : cell.col;

        pictoItems.push({
          row: newRow,
          col: newCol,
          countIdx: remainder,
          itemIdx: i,
          item: currItem,
          fractionalStart: 0,
          fractionalEnd: remainder
        });
      }
    }
  }
  return pictoItems;
}